import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { TreeSelectModule } from 'primeng/treeselect';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// Services
import { LanguagesService } from '@generated/services/languages.service';
import { AdminService } from '@generated/services/admin.service';
import { ModelsService } from '@generated/services/models.service';
import { AgentsService } from '@generated/services/agents.service';
import { NotificationService } from '@core/services/notification.service';

// Directives
import { PreventOverlayCloseOnScrollDirective } from '@shared/directives/prevent-overlay-close-on-scroll';

// Components
import { ModelSelector } from '@shared/components/model-selector/model-selector';
import { ModelSelectorButton } from '@shared/components/model-selector-button/model-selector-button';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { LANGUAGE_LOCALE_FIELDS } from '@core/assistant/assistant-catalog';

// Models
import {
  LanguageLocaleDto,
  GetLanguagesResult,
  GetLocalesResult,
  GetModelsHierarchyResult,
  UpsertLanguageLocaleCommand,
  UpsertLanguageLocaleOptionDto,
  AgentSummary
} from '@generated/models';

@Component({
  selector: 'app-language-locale-edit',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    FormsModule,
    ButtonModule,
    CardModule,
    DrawerModule,
    SelectModule,
    MultiSelectModule,
    TreeSelectModule,
    ToggleSwitchModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    TooltipModule,
    ConfirmDialogModule,
    PreventOverlayCloseOnScrollDirective,
    ModelSelectorButton
],
  providers: [ConfirmationService, DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './language-locale-edit.html',
  styleUrl: './language-locale-edit.css'
})
export class LanguageLocaleEditComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = LANGUAGE_LOCALE_FIELDS;

  private languagesService = inject(LanguagesService);
  private adminService = inject(AdminService);
  private modelsService = inject(ModelsService);
  private agentsService = inject(AgentsService);
  private notificationService = inject(NotificationService);
  private confirmationService = inject(ConfirmationService);
  private dialogService = inject(DialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  private modelSelectorRef: DynamicDialogRef | undefined;

  languageLocale: LanguageLocaleDto | null = null;
  languages: GetLanguagesResult | null = null;
  locales: GetLocalesResult | null = null;
  modelsHierarchy: GetModelsHierarchyResult | null = null;
  modelsIncludeAll = false;

  // Default agent data
  defaultAgent: AgentSummary | null = null;
  allAgents: AgentSummary[] = [];
  agentOptions: { label: string; value: string }[] = [];

  isLoading = false;
  isSaving = false;

  editingLanguageLocale: UpsertLanguageLocaleCommand = this.createEmpty();

  selectedLlmNode: any = null;
  selectedSttNode: any = null;
  selectedTtsNode: any = null;
  selectedVadNode: any = null;

  llmTreeNodes: any[] = [];
  sttTreeNodes: any[] = [];
  ttsTreeNodes: any[] = [];
  vadTreeNodes: any[] = [];

  languageOptions: { label: string; value: string }[] = [];
  localeOptions: { label: string; value: string }[] = [];

  showOptionDrawer = false;
  editingOption: UpsertLanguageLocaleOptionDto = this.createEmptyOption();
  editingOptionIndex: number = -1;
  isEditingOption = false;

  get isEditMode(): boolean {
    return !!this.languageLocale;
  }

  get isNewMode(): boolean {
    return this.route.snapshot.paramMap.get('id') === 'new';
  }

  get isFormValid(): boolean {
    return !!(
      this.editingLanguageLocale.language_id &&
      this.editingLanguageLocale.locale_id
    );
  }


  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.loadLanguageLocale(id);
    } else {
      this.loadReferenceData();
    }
  }

  private loadLanguageLocale(id: string): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    forkJoin({
      languageLocale: this.languagesService.apiLanguagesLanguageLocalesIdGet({ id }),
      languages: this.languagesService.apiLanguagesGet(),
      locales: this.languagesService.apiLanguagesLocalesGet(),
      modelsHierarchy: this.modelsService.apiModelsHierarchyGet({ includeAll: this.modelsIncludeAll }),
      allAgents: this.agentsService.apiAgentsGet()
    }).subscribe({
      next: (data) => {
        this.languages = data.languages;
        this.locales = data.locales;
        this.modelsHierarchy = data.modelsHierarchy;
        this.allAgents = data.allAgents.agents || [];

        this.languageLocale = data.languageLocale;

        // Check if default agent is in allAgents list, if not - load it separately
        if (this.languageLocale?.default_agent_id) {
          const defaultAgentInList = this.allAgents.find(a => a.id === this.languageLocale!.default_agent_id);
          if (defaultAgentInList) {
            this.defaultAgent = defaultAgentInList;
            this.buildAgentOptions();
          } else {
            this.loadDefaultAgentById(this.languageLocale.default_agent_id);
          }
        } else {
          this.buildAgentOptions();
        }

        if (this.languageLocale) {
          this.editingLanguageLocale = {
            id: this.languageLocale.id,
            language_id: this.languageLocale.language_id,
            locale_id: this.languageLocale.locale_id,
            default_llm_version_id: this.languageLocale.default_llm_version_id,
            default_stt_version_id: this.languageLocale.default_stt_version_id,
            default_tts_version_id: this.languageLocale.default_tts_version_id,
            default_vad_version_id: this.languageLocale.default_vad_version_id,
            default_agent_id: this.languageLocale.default_agent_id,
            is_active: this.languageLocale.is_active,
            is_default: this.languageLocale.is_default,
            default_first_message: this.languageLocale.default_first_message,
            options: this.languageLocale.options?.map(opt => ({
              id: opt.id,
              name: opt.name,
              description: opt.description,
              system_prompt: opt.system_prompt
            })) || []
          };
        }

        this.buildOptions();
        this.buildTreeNodes();

        if (this.languageLocale) {
          this.selectedLlmNode = this.findNodeByVersionId(this.llmTreeNodes, this.languageLocale.default_llm_version_id);
          this.selectedSttNode = this.findNodeByVersionId(this.sttTreeNodes, this.languageLocale.default_stt_version_id);
          this.selectedTtsNode = this.findNodeByVersionId(this.ttsTreeNodes, this.languageLocale.default_tts_version_id);
          this.selectedVadNode = this.findNodeByVersionId(this.vadTreeNodes, this.languageLocale.default_vad_version_id);
        }

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load language locale:', error);
        this.notificationService.showError('Failed to load language locale');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadReferenceData(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    forkJoin({
      languages: this.languagesService.apiLanguagesGet(),
      locales: this.languagesService.apiLanguagesLocalesGet(),
      modelsHierarchy: this.modelsService.apiModelsHierarchyGet({ includeAll: this.modelsIncludeAll }),
      allAgents: this.agentsService.apiAgentsGet()
    }).subscribe({
      next: (data) => {
        this.languages = data.languages;
        this.locales = data.locales;
        this.modelsHierarchy = data.modelsHierarchy;
        this.allAgents = data.allAgents.agents || [];

        this.buildAgentOptions();
        this.buildOptions();
        this.buildTreeNodes();

        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load reference data:', error);
        this.notificationService.showError('Failed to load reference data');
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private buildOptions(): void {
    this.buildLanguageOptions();
    this.buildLocaleOptions();
  }

  private loadDefaultAgentById(agentId: string): void {
    this.agentsService.apiAgentsIdGet({ id: agentId }).subscribe({
      next: (agent) => {
        this.defaultAgent = agent;
        this.buildAgentOptions();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load default agent:', error);
        this.buildAgentOptions();
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private buildAgentOptions(): void {
    // Combine default agent and all agents with uniqueness by id
    const agentsMap = new Map<string, AgentSummary>();

    // Add default agent first if it exists
    if (this.defaultAgent?.id) {
      agentsMap.set(this.defaultAgent.id, this.defaultAgent);
    }

    // Add all agents
    this.allAgents.forEach(agent => {
      if (agent.id && !agentsMap.has(agent.id)) {
        agentsMap.set(agent.id, agent);
      }
    });

    // Build options with format: "Agent Name (author@email.com)"
    this.agentOptions = Array.from(agentsMap.values())
      .map(agent => ({
        label: `${agent.title || 'Untitled'} (${agent.created_by_email || 'unknown'})`,
        value: agent.id!
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private buildLanguageOptions(): void {
    if (!this.languages?.languages) {
      this.languageOptions = [];
      return;
    }

    this.languageOptions = this.languages.languages.map(lang => {
      const nativeName = lang.native_name || '';
      const englishName = lang.name || '';
      const label = nativeName && englishName && nativeName !== englishName
        ? `${nativeName} (${englishName})`
        : nativeName || englishName || lang.code || '';

      return {
        label: label,
        value: lang.id || ''
      };
    }).filter(opt => opt.value);
  }

  private buildLocaleOptions(): void {
    if (!this.locales?.locales) {
      this.localeOptions = [];
      return;
    }

    this.localeOptions = this.locales.locales.map(locale => ({
      label: `${locale.flag || ''} ${locale.name || locale.code || ''}`,
      value: locale.id || ''
    })).filter(opt => opt.value);
  }

  private buildTreeNodes(): void {
    this.llmTreeNodes = this.buildTreeForModelType('llm');
    this.sttTreeNodes = this.buildTreeForModelType('stt');
    this.ttsTreeNodes = this.buildTreeForModelType('tts');
    this.vadTreeNodes = this.buildTreeForModelType('vad');
  }

  private buildTreeForModelType(modelTypeCode: string): any[] {
    if (!this.modelsHierarchy?.model_types) return [];

    const modelType = this.modelsHierarchy.model_types.find(mt => mt.code === modelTypeCode);
    if (!modelType?.gateways) return [];

    return modelType.gateways
      .map(gateway => {
        const children = gateway.models?.map(model => {
          const label = model.author?.name ? `${model.author.name} ${model.name}` : (model.name || '');
          return {
            key: model.id,
            label,
            data: `${label} Model`,
            icon: 'pi pi-fw pi-box',
            selectable: false,
            children: model.versions?.map(version => ({
              key: version.id,
              label: `${model.name} ${version.name}`,
              data: `${model.name} ${version.name}`,
              icon: 'pi pi-fw pi-file',
              selectable: true
            })) || []
          };
        }) || [];

        if (children.length > 0) {
          return {
            key: gateway.id,
            label: gateway.name,
            data: `${gateway.name} Gateway`,
            icon: 'pi pi-fw pi-building',
            selectable: false,
            children: children
          };
        }
        return null;
      })
      .filter(gateway => gateway !== null);
  }

  private findNodeByVersionId(nodes: any[], versionId?: string): any {
    if (!versionId) return null;

    const findNode = (nodes: any[], key: string): any => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
          const found = findNode(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    return findNode(nodes, versionId);
  }

  onLlmNodeSelect(event: any): void {
    this.editingLanguageLocale.default_llm_version_id = event?.key;
  }

  onSttNodeSelect(event: any): void {
    this.editingLanguageLocale.default_stt_version_id = event?.key;
  }

  onTtsNodeSelect(event: any): void {
    this.editingLanguageLocale.default_tts_version_id = event?.key;
  }

  onVadNodeSelect(event: any): void {
    this.editingLanguageLocale.default_vad_version_id = event?.key;
  }

  onAddOption(): void {
    this.editingOption = this.createEmptyOption();
    this.editingOptionIndex = -1;
    this.isEditingOption = false;
    this.showOptionDrawer = true;
    this.cdr.markForCheck();
  }

  onEditOption(option: UpsertLanguageLocaleOptionDto, index: number): void {
    this.editingOption = { ...option };
    this.editingOptionIndex = index;
    this.isEditingOption = true;
    this.showOptionDrawer = true;
    this.cdr.markForCheck();
  }

  onSaveOption(): void {
    if (!this.editingOption.name?.trim()) {
      this.notificationService.showError('Please enter option name');
      return;
    }

    if (!this.editingLanguageLocale.options) {
      this.editingLanguageLocale.options = [];
    }

    if (this.editingOptionIndex >= 0) {
      this.editingLanguageLocale.options[this.editingOptionIndex] = this.editingOption;
    } else {
      this.editingLanguageLocale.options.push(this.editingOption);
    }

    this.showOptionDrawer = false;
    this.cdr.markForCheck();
  }

  onCancelOption(): void {
    this.showOptionDrawer = false;
    this.cdr.markForCheck();
  }

  onDeleteOption(index: number): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this option?',
      header: 'Delete Option',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.editingLanguageLocale.options?.splice(index, 1);
        this.cdr.markForCheck();
      }
    });
  }

  onSave(): void {
    if (!this.isFormValid) {
      this.notificationService.showError('Please fill in all required fields');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    this.adminService.apiAdminLanguageLocalesPost({
      body: this.editingLanguageLocale
    }).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          this.isEditMode ? 'Language locale updated successfully' : 'Language locale created successfully'
        );
        this.router.navigate(['/admin/language-locales']);
      },
      error: (error) => {
        console.error('Failed to save language locale:', error);
        this.notificationService.showError('Failed to save language locale');
        this.isSaving = false;
        this.cdr.markForCheck();
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/language-locales']);
  }

  private createEmpty(): UpsertLanguageLocaleCommand {
    return {
      language_id: undefined,
      locale_id: undefined,
      default_llm_version_id: undefined,
      default_stt_version_id: undefined,
      default_tts_version_id: undefined,
      default_vad_version_id: undefined,
      default_agent_id: null,
      is_active: true,
      is_default: false,
      default_first_message: undefined,
      options: []
    };
  }

  private createEmptyOption(): UpsertLanguageLocaleOptionDto {
    return {
      name: undefined,
      description: undefined,
      system_prompt: undefined
    };
  }


  onLlmModelSelected(versionId: string): void {
    this.editingLanguageLocale.default_llm_version_id = versionId;
    this.cdr.markForCheck();
  }

  onSttModelSelected(versionId: string): void {
    this.editingLanguageLocale.default_stt_version_id = versionId;
    this.cdr.markForCheck();
  }

  onTtsModelSelected(versionId: string): void {
    this.editingLanguageLocale.default_tts_version_id = versionId;
    this.cdr.markForCheck();
  }

  onVadModelSelected(versionId: string): void {
    this.editingLanguageLocale.default_vad_version_id = versionId;
    this.cdr.markForCheck();
  }

  onModelsIncludeAllChange(includeAll: boolean): void {
    this.modelsIncludeAll = includeAll;
    this.modelsService.apiModelsHierarchyGet({ includeAll }).subscribe({
      next: (hierarchy) => {
        this.modelsHierarchy = hierarchy;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notificationService.showError(
          $localize`:@@languageLocale.models.refreshFailed:Не удалось обновить список моделей`
        );
      }
    });
  }
}
